import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

// ── Types ─────────────────────────────────────────────────────────

export interface Geofence {
  id: string
  name: string
  description?: string
  shape: 'polygon' | 'circle'
  geometry: any
  color: string
  is_active: boolean
  created_at: string
}

export interface GeofenceEvent {
  id: string
  geofence_id: string
  device_id: string
  event_type: 'enter' | 'exit'
  latitude: number | null
  longitude: number | null
  occurred_at: string
}

export interface Trip {
  id: string
  device_id: string
  driver_user_id: string | null
  started_at: string
  ended_at: string | null
  start_latitude: number | null
  start_longitude: number | null
  end_latitude: number | null
  end_longitude: number | null
  distance_km: number
  duration_sec: number
  max_speed_kph: number
  avg_speed_kph: number
  status: string
}

export interface Job {
  id: string
  reference: string
  customer_name: string
  customer_phone: string
  pickup_address: string
  dropoff_address: string
  pickup_latitude: number | null
  pickup_longitude: number | null
  dropoff_latitude: number | null
  dropoff_longitude: number | null
  assigned_driver_id: string | null
  assigned_device_id: string | null
  scheduled_at: string | null
  status: string
  notes: string
  created_at: string
  // Wave 1 E1.2 — Dispatcher Console
  sla_due_at?: string | null
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  started_at?: string | null
  arrived_at?: string | null
  completed_at?: string | null
  sla_status?: 'on_track' | 'at_risk' | 'breached' | 'met'
  skill_tags?: string[]
}

export interface DeviceCommand {
  id: string
  device_id: string
  command: string
  status: string
  issued_by: string | null
  issued_at: string
  completed_at: string | null
  error: string
}

export interface MaintenanceSchedule {
  id: string
  device_id: string
  task: string
  every_km: number | null
  every_days: number | null
  next_due_km: number | null
  next_due_at: string | null
  is_active: boolean
}

export interface FuelRecord {
  id: string
  device_id: string
  recorded_at: string
  level_l: number | null
  delta_l: number | null
  cost: number | null
  source: string
  flagged_theft: boolean
}

export interface DriverScore {
  id: string
  driver_user_id: string
  period: string
  score: number
  trips: number
  distance_km: number
  harsh_brake_count: number
  harsh_accel_count: number
  overspeed_count: number
  idle_minutes: number
}

// ── Geofences ─────────────────────────────────────────────────────

export function useGeofences() {
  return useQuery({
    queryKey: ['geofences'],
    queryFn: () => api.get('/geofences').then((r) => unwrapList<Geofence>(r.data)),
  })
}

export function useCreateGeofence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Geofence>) => api.post('/geofences', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  })
}

export function useDeleteGeofence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/geofences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  })
}

// ── Trips ─────────────────────────────────────────────────────────

export function useTrips(deviceId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['trips', deviceId ?? null, from ?? null, to ?? null],
    queryFn: () => {
      const params: Record<string, string> = {}
      if (deviceId) params.device_id = deviceId
      if (from) params.from = from
      if (to) params.to = to
      return api.get('/trips', { params })
        .then((r) => unwrapList<Trip>(r.data))
    },
  })
}

// ── Jobs ──────────────────────────────────────────────────────────

export function useJobs(driverId?: string) {
  return useQuery({
    queryKey: ['jobs', driverId ?? null],
    queryFn: () =>
      api.get('/jobs', { params: driverId ? { driver_id: driverId } : undefined })
        .then((r) => unwrapList<Job>(r.data)),
  })
}

export function useCreateJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Job>) => api.post('/jobs', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export function useUpdateJobStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put(`/jobs/${id}/status`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export function useDeleteJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/jobs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export function useAssignJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, driver_id, device_id }: { id: string; driver_id?: string; device_id?: string }) =>
      api.put(`/jobs/${id}/assign`, { driver_id, device_id }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

// ── Device commands ──────────────────────────────────────────────

export function useDeviceCommands(deviceId?: string) {
  return useQuery({
    queryKey: ['commands', deviceId ?? null],
    queryFn: () =>
      api.get('/commands', { params: deviceId ? { device_id: deviceId } : undefined })
        .then((r) => unwrapList<DeviceCommand>(r.data)),
  })
}

export function useIssueCommand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, command, payload }: { deviceId: string; command: string; payload?: unknown }) =>
      api.post(`/devices/${deviceId}/commands`, { command, payload }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commands'] }),
  })
}

// ── Maintenance ───────────────────────────────────────────────────

export function useMaintenance() {
  return useQuery({
    queryKey: ['maintenance'],
    queryFn: () => api.get('/maintenance').then((r) => unwrapList<MaintenanceSchedule>(r.data)),
  })
}

export function useCreateMaintenance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<MaintenanceSchedule>) => api.post('/maintenance', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance'] }),
  })
}

// ── Fuel ──────────────────────────────────────────────────────────

export function useFuel(deviceId?: string) {
  return useQuery({
    queryKey: ['fuel', deviceId ?? null],
    queryFn: () =>
      api.get('/fuel', { params: deviceId ? { device_id: deviceId } : undefined })
        .then((r) => unwrapList<FuelRecord>(r.data)),
  })
}

// ── Driver scores ────────────────────────────────────────────────

export function useDriverScores() {
  return useQuery({
    queryKey: ['driver-scores'],
    queryFn: () => api.get('/driver-scores').then((r) => unwrapList<DriverScore>(r.data)),
  })
}

// ── Timezones ─────────────────────────────────────────────────────

export interface TimezoneRow { name: string; offset_sec: number; offset_dst: number }

export function useTimezones() {
  return useQuery({
    queryKey: ['timezones'],
    queryFn: () => api.get('/timezones').then((r) => unwrapList<TimezoneRow>(r.data)),
    staleTime: 24 * 60 * 60 * 1000, // doesn't change between sessions
  })
}

// ── My tenant subscription ────────────────────────────────────────

export interface TenantInfo {
  id: string
  name: string
  status: string
  subscription_plan: string
  subscription_expires_at: string | null
  currency: string
}

export function useMyTenant(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.get(`/tenants/${tenantId}`).then((r) => r.data as TenantInfo),
    enabled: !!tenantId,
  })
}

export function useRenewTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tenantId, days }: { tenantId: string; days: number }) =>
      api.post(`/tenants/${tenantId}/renew`, { days }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant'] }),
  })
}

// ── Fuel Analytics ───────────────────────────────────────────────

export interface FuelAnalytics {
  total_consumed_l: number
  total_cost: number
  cost_per_km: number
  refill_count: number
  theft_count: number
  avg_consumption_per_100km: number
}

export function useFuelAnalytics(deviceId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['fuel-analytics', deviceId, from, to],
    queryFn: () => api.get('/fuel/analytics', { params: { device_id: deviceId, from, to } }).then(r => r.data as FuelAnalytics),
    enabled: !!deviceId,
  })
}

// ── Driver Leaderboard ───────────────────────────────────────────

export interface LeaderboardEntry {
  driver_user_id: string
  score: number
  trips: number
  distance_km: number
  rank: number
}

export function useLeaderboard(period: 'weekly' | 'monthly' = 'weekly') {
  return useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => api.get('/driver-scores/leaderboard', { params: { period } }).then(r => r.data?.data ?? []) as Promise<LeaderboardEntry[]>,
  })
}

// ── Trip Comparison ──────────────────────────────────────────────

export function useTripComparison(tripIds: string[]) {
  return useQuery({
    queryKey: ['trip-comparison', tripIds],
    queryFn: () => api.get('/trips/compare', { params: { trip_ids: tripIds.join(',') } }).then(r => r.data?.data ?? []) as Promise<Trip[]>,
    enabled: tripIds.length >= 2,
  })
}
